import { evaluateExpression, FunctionRegistry, isTruthy } from '../expressions/expressions.js';
import { add, compare, decimal, divide, toApproximateNumber, toText } from '../pricing/decimal.js';
const functions = FunctionRegistry.standard();
function attempt(expression, scope) {
    try {
        return evaluateExpression(expression, scope, { functions });
    }
    catch {
        return undefined;
    }
}
function holds(expression, scope) {
    return expression === undefined || expression === null ? true : isTruthy(attempt(expression, scope));
}
/** Couche de mise en page déclarée (1.8.0) ; `undefined` pour un gabarit d'avant. */
export function layoutOf(template) {
    return template.layout;
}
/** Étapes de 1.6.0, lues à l'identique (compatibilité ascendante). */
function legacySteps(template) {
    const steps = template.steps ?? [];
    return steps.filter((step) => Array.isArray(step.sections) && step.sections.length > 0).map((step) => ({ page_id: step.step_id, label: step.label, sections: [...step.sections] }));
}
/** Le gabarit déclare-t-il une présentation en pages (couche de mise en page ou étapes de 1.6.0) ? */
export function hasPagedPresentation(template) {
    return layoutOf(template) !== undefined || legacySteps(template).length > 0;
}
/**
 * Pages d'affichage, dans l'ordre (§2.3) : celles de `layout.pages`, sinon les étapes de 1.6.0, sinon
 * une seule page avec toutes les sections. Une section citée nulle part va à la dernière page.
 */
export function formPages(template) {
    const declared = layoutOf(template)?.pages?.map((page) => ({
        page_id: page.page_id,
        label: page.label,
        sections: [...page.sections],
        ...(page.visible_when === undefined ? {} : { visible_when: page.visible_when }),
        ...(page.allow_skip === undefined ? {} : { allow_skip: page.allow_skip }),
        ...(page.counter === undefined ? {} : { counter: { aggregate_ref: page.counter.aggregate_ref } }),
    })) ?? legacySteps(template);
    const all = template.sections.map((section) => section.section_id);
    if (declared.length === 0)
        return [{ page_id: 'main', label: template.label, sections: all }];
    const cited = new Set(declared.flatMap((page) => page.sections));
    const rest = all.filter((id) => !cited.has(id));
    if (rest.length === 0)
        return declared;
    return declared.map((page, index) => (index === declared.length - 1 ? { ...page, sections: [...page.sections, ...rest] } : page));
}
/**
 * Mode de navigation dans un format (§2.2) : `layout.navigation.mode`, sinon `steps_with_summary` pour
 * une fiche à étapes de 1.6.0, sinon `single`. Portrait, mobile et web suivent leur règle déclarée
 * (`keep`, `wizard`, `single`) ; le web suit le paysage sans règle.
 */
export function navigationMode(template, format) {
    const navigation = layoutOf(template)?.navigation;
    const base = navigation?.mode ?? (legacySteps(template).length > 0 ? 'steps_with_summary' : 'single');
    const rule = format === 'landscape' ? undefined : navigation?.[format];
    if (rule === undefined || rule === 'keep')
        return base;
    return rule;
}
/** Une page est-elle visible (`visible_when` sur `$answers` et `$inputs`) ? */
export function pageVisible(page, answers, inputs = {}) {
    return holds(page.visible_when, { answers, inputs });
}
export function visiblePages(template, answers, inputs = {}) {
    return formPages(template).filter((page) => pageVisible(page, answers, inputs));
}
/** Sections des pages cachées : ni exigées ni lues, au client comme au serveur. */
export function hiddenPageSections(template, answers, inputs = {}) {
    const hidden = new Set();
    if (layoutOf(template)?.pages === undefined)
        return hidden;
    for (const page of formPages(template))
        if (!pageVisible(page, answers, inputs))
            for (const id of page.sections)
                hidden.add(id);
    return hidden;
}
function empty(value) {
    return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
}
function requiredIn(entry, answers) {
    // Une obligation conditionnelle nulle n'oblige pas, comme au serveur (readForm).
    return entry.field.required === true || (entry.required_when !== undefined && entry.required_when !== null && holds(entry.required_when, { answers }));
}
function visibleIn(entry, answers) {
    return holds(entry.visible_when, { answers });
}
/**
 * Manques d'une page (contrôle par étape d'un assistant, pastilles des onglets) : champs obligatoires
 * visibles vides, preuves exigées absentes, nombre de lignes d'une série, champs obligatoires de chaque
 * ligne ou de chaque élément. Chemins de la lecture du serveur (`answers.x`, `answers.s.0.x`,
 * `evidence.x`). Une aide : le serveur revalide tout à la complétion.
 */
export function pageMissing(template, page, answers, evidence = {}, inputs = {}) {
    const missing = [];
    for (const section of template.sections) {
        if (!page.sections.includes(section.section_id) || !holds(section.visible_when, { answers }))
            continue;
        const id = section.section_id;
        if (section.series !== undefined) {
            const rows = Array.isArray(answers[id]) ? answers[id] : [];
            if (rows.length < (section.series.min_count ?? 0))
                missing.push(`answers.${id}`);
            else if (section.series.exit?.required === true && !rows.some((row) => row['exit'] === true))
                missing.push(`answers.${id}`);
            rows.forEach((row, index) => {
                for (const entry of section.fields)
                    if (entry.compute === undefined && visibleIn(entry, row) && requiredIn(entry, row) && empty(row[entry.field.field_id]))
                        missing.push(`answers.${id}.${index}.${entry.field.field_id}`);
            });
            continue;
        }
        if (section.repeat !== undefined) {
            const items = Array.isArray(inputs[section.repeat.items_input]) ? inputs[section.repeat.items_input].map(String) : [];
            const given = Array.isArray(answers[id]) ? answers[id] : [];
            items.forEach((itemId, index) => {
                const answer = given.find((entry) => entry['item_id'] === itemId) ?? {};
                for (const entry of section.fields)
                    if (entry.compute === undefined && visibleIn(entry, answer) && requiredIn(entry, answer) && empty(answer[entry.field.field_id]))
                        missing.push(`answers.${id}.${index}.${entry.field.field_id}`);
            });
            continue;
        }
        for (const entry of section.fields) {
            if (entry.compute !== undefined || !visibleIn(entry, answers))
                continue;
            const fieldId = entry.field.field_id;
            if (requiredIn(entry, answers) && empty(answers[fieldId]))
                missing.push(`answers.${fieldId}`);
            if (entry.evidence !== undefined && entry.evidence.min_count > 0 && holds(entry.evidence.when, { answers }) && (evidence[fieldId]?.length ?? 0) < entry.evidence.min_count)
                missing.push(`evidence.${fieldId}`);
        }
    }
    return missing;
}
/** Page d'un chemin d'erreur ou d'avertissement (`answers.s.3.x`, `answers.x`, `evidence.x`). */
export function pageOfPath(template, path) {
    const [, first] = path.split('.');
    if (first === undefined)
        return undefined;
    const owner = template.sections.find((section) => section.section_id === first && (section.series !== undefined || section.repeat !== undefined))
        ?? template.sections.find((section) => section.series === undefined && section.repeat === undefined && section.fields.some((entry) => entry.field.field_id === first));
    if (owner === undefined)
        return undefined;
    return formPages(template).find((page) => page.sections.includes(owner.section_id))?.page_id;
}
function numericText(value) {
    const raw = value !== null && typeof value === 'object' && 'value' in value ? value['value'] : value;
    if (typeof raw === 'number' && Number.isFinite(raw))
        return decimal(raw);
    if (typeof raw === 'string' && /^-?\d+(\.\d+)?$/.test(raw.trim()))
        return decimal(raw.trim());
    return null;
}
/** Clé d'égalité d'une valeur (doublons, compte distinct, regroupement) : texte nu, sans espaces. */
export function valueKey(value) {
    if (value === undefined || value === null)
        return null;
    const raw = typeof value === 'object' && 'value' in value ? value['value'] : value;
    const key = typeof raw === 'string' ? raw.trim() : typeof raw === 'number' || typeof raw === 'boolean' ? String(raw) : JSON.stringify(raw);
    return key === '' ? null : key;
}
function trimmed(value) {
    const text = toText(value);
    return text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text;
}
function rowsOf(template, answers, source) {
    const section = template.sections.find((candidate) => candidate.section_id === source);
    if (section === undefined)
        return [];
    const rows = Array.isArray(answers[source]) ? answers[source] : [];
    // Une ligne sans aucune réponse ne part pas : elle ne compte pas (ADR-146 addendum (28)).
    return rows
        .map((row) => Object.fromEntries(Object.entries(row ?? {}).filter(([key]) => key !== 'exit' && key !== 'item_id' && key !== 'children')))
        .filter((row) => !Object.entries(row).every(([, value]) => value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)));
}
function unitOf(template, source, field) {
    if (field === undefined)
        return undefined;
    const entry = template.sections.find((section) => section.section_id === source)?.fields.find((candidate) => candidate.field.field_id === field);
    return entry?.field?.unit;
}
/** Agrégats déclarés (`layout.aggregates`), calculés sur les réponses. */
export function formAggregates(template, answers) {
    return (layoutOf(template)?.aggregates ?? []).map((aggregate) => {
        const rows = rowsOf(template, answers, aggregate.source).filter((row) => aggregate.where === undefined || holds(aggregate.where, { row, answers }));
        const unit = aggregate.unit ?? unitOf(template, aggregate.source, aggregate.field);
        const base = { aggregate_id: aggregate.aggregate_id, op: aggregate.op, label: aggregate.label, ...(unit === undefined || aggregate.op.startsWith('count') || aggregate.op === 'duplicates' ? {} : { unit }) };
        const field = aggregate.field ?? '';
        const count = (n) => ({ ...base, value: n, text: String(n) });
        switch (aggregate.op) {
            case 'count': {
                if (aggregate.group_by === undefined)
                    return count(rows.length);
                const by = new Map();
                for (const row of rows) {
                    const key = valueKey(row[aggregate.group_by]);
                    if (key !== null)
                        by.set(key, (by.get(key) ?? 0) + 1);
                }
                return { ...count(rows.length), by: [...by].map(([key, n]) => ({ key, count: n })) };
            }
            case 'count_distinct':
                return count(new Set(rows.map((row) => valueKey(row[field])).filter((key) => key !== null)).size);
            case 'duplicates': {
                const seen = new Map();
                for (const row of rows) {
                    const key = valueKey(row[field]);
                    if (key !== null)
                        seen.set(key, (seen.get(key) ?? 0) + 1);
                }
                return count([...seen.values()].filter((n) => n > 1).length);
            }
            default: {
                const values = rows.map((row) => numericText(row[field])).filter((value) => value !== null);
                if (aggregate.op === 'sum' || aggregate.op === 'avg') {
                    const total = values.reduce((sum, value) => add(sum, value), decimal(0));
                    if (aggregate.op === 'sum')
                        return { ...base, value: toApproximateNumber(total), text: trimmed(total) };
                    if (values.length === 0)
                        return { ...base, value: 0, text: '' };
                    const scale = Math.max(0, ...values.map((value) => value.scale)) + 2;
                    const mean = divide(total, decimal(values.length), scale);
                    return { ...base, value: toApproximateNumber(mean), text: trimmed(mean) };
                }
                if (values.length === 0)
                    return { ...base, value: 0, text: '' };
                const pick = values.reduce((best, value) => ((aggregate.op === 'min' ? compare(value, best) < 0 : compare(value, best) > 0) ? value : best));
                return { ...base, value: toApproximateNumber(pick), text: trimmed(pick) };
            }
        }
    });
}
/** `$aggregates` des expressions d'affichage : identifiant → valeur. */
export function aggregateScope(values) {
    return Object.fromEntries(values.map((value) => [value.aggregate_id, value.value]));
}
function textOf(label, fallback) {
    if (label !== null && typeof label === 'object') {
        const map = label;
        return map['fr'] ?? Object.values(map)[0] ?? fallback;
    }
    return fallback;
}
/**
 * Avertissements déclarés par `layout.warnings` : par ligne d'une source (`$row`, `$answers`), sinon
 * sur la fiche (`$answers`, `$aggregates`). Chemin : la ligne (`answers.<source>.<n>`) ou la fiche (`answers`).
 */
export function layoutWarnings(template, answers) {
    const declared = layoutOf(template)?.warnings ?? [];
    if (declared.length === 0)
        return [];
    const aggregates = aggregateScope(formAggregates(template, answers));
    const out = [];
    for (const warning of declared) {
        const message = textOf(warning.message, warning.warning_id);
        if (warning.source === undefined) {
            if (holds(warning.when, { answers, aggregates }) && warning.when !== undefined)
                out.push({ field: 'answers', code: 'FORM_WARNING', warning_id: warning.warning_id, message });
            continue;
        }
        rowsOf(template, answers, warning.source).forEach((row, index) => {
            if (isTruthy(attempt(warning.when, { row, answers, aggregates })))
                out.push({ field: `answers.${warning.source}.${index}`, code: 'FORM_WARNING', warning_id: warning.warning_id, message });
        });
    }
    return out;
}
// ---- Placements, blocs, liaisons.
/** Placement d'une section ; `{}` sans déclaration. */
export function placementOf(template, sectionId) {
    return layoutOf(template)?.placements?.[sectionId] ?? {};
}
/** Une série se présente-t-elle en tableau dense (registre de 1.6.0 ou `presentation.as: table`) ? */
export function seriesAsTable(template, section) {
    if (section.series === undefined)
        return false;
    const as = placementOf(template, section.section_id).presentation?.as;
    if (as !== undefined)
        return as === 'table';
    return section.series.presentation === 'ledger';
}
/** Blocs d'une page : ceux qui la citent, et les blocs persistants (sans `page_ids`). */
export function blocksOn(template, pageId) {
    return (layoutOf(template)?.blocks ?? []).filter((block) => block.page_ids === undefined || block.page_ids.includes(pageId));
}
/** Nom et plage d'un composant cité par un bloc (`ObjectBanner@^1.0.0`). */
export function blockComponent(block) {
    const at = block.component.lastIndexOf('@');
    if (at < 0)
        return { name: block.component, range: '*' };
    return { name: block.component.slice(0, at), range: block.component.slice(at + 1) };
}
/** Valeur d'une liaison par chemin (`$answers.arrival.hunter`), dans une portée `{ answers, aggregates, … }`. */
export function resolveBinding(path, scope) {
    const [root, ...rest] = path.slice(1).split('.');
    let value = scope[root ?? ''];
    for (const part of rest) {
        if (value === null || value === undefined)
            return undefined;
        value = Array.isArray(value) && /^\d+$/.test(part) ? value[Number(part)] : typeof value === 'object' ? value[part] : undefined;
    }
    return value;
}
/**
 * Texte d'affichage d'une propriété du gabarit (`confirmation.message`, `success.message`) : un libellé
 * localisé, un texte, ou une expression (`$concat` sur `$aggregates`, `$answers`, `$result`).
 */
export function displayText(value, scope, language = 'fr') {
    if (value === undefined || value === null)
        return undefined;
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).every((key) => /^[a-z]{2}(-[a-z]{2})?$/.test(key))) {
        const map = value;
        return map[language] ?? map['fr'] ?? Object.values(map)[0];
    }
    const result = attempt(value, scope);
    if (result === undefined || result === null)
        return undefined;
    return typeof result === 'string' ? result : String(result);
}
/** Une entrée, une section ou un bloc est-il en lecture seule (`readonly_when`) ? Présentation seulement. */
export function readonlyHolds(expression, scope) {
    return expression !== undefined && expression !== null && isTruthy(attempt(expression, scope));
}
/** La confirmation s'applique-t-elle (`confirmation.when`, vraie par défaut) ? */
export function confirmationApplies(template, answers) {
    const confirmation = layoutOf(template)?.confirmation;
    if (confirmation === undefined)
        return false;
    return confirmation.when === undefined ? true : holds(confirmation.when, { answers, aggregates: aggregateScope(formAggregates(template, answers)) });
}
/**
 * `$aggregates` d'un texte d'affichage (`confirmation.message`, `success.message`) : la valeur exacte en
 * texte, au séparateur décimal de la langue (`412,75` en français). Une condition lit les nombres
 * ({@link aggregateScope}) ; un texte lit ceci.
 */
export function aggregateTextScope(values, language = 'fr') {
    return Object.fromEntries(values.map((value) => [value.aggregate_id, language === 'en' ? value.text : value.text.replace('.', ',')]));
}
function rowsIn(answers, sectionId) {
    return Array.isArray(answers[sectionId]) ? answers[sectionId] : [];
}
/** Une ligne est-elle dans l'onglet (`where` sur `$row` et `$answers`, comme un agrégat) ? Sans `where` : toujours. */
export function tabHolds(tab, row, answers) {
    return tab.where === undefined ? true : holds(tab.where, { row, answers });
}
/** Onglets de filtre d'une liste et leur compteur ; `[]` sans onglets déclarés. */
export function listTabs(template, sectionId, answers) {
    const tabs = (placementOf(template, sectionId).tabs ?? []);
    const rows = rowsIn(answers, sectionId);
    return tabs.map((tab) => ({ tab_id: tab.tab_id, label: tab.label, ...(tab.tone === undefined ? {} : { tone: tab.tone }), count: rows.filter((row) => tabHolds(tab, row, answers)).length }));
}
/** Index des lignes montrées par l'onglet (`tabId` inconnu ou absent : le premier onglet, sinon toutes). */
export function tabRowIndexes(template, sectionId, answers, tabId) {
    const tabs = (placementOf(template, sectionId).tabs ?? []);
    const tab = tabs.find((candidate) => candidate.tab_id === tabId) ?? tabs[0];
    const rows = rowsIn(answers, sectionId);
    return rows.map((row, index) => (tab === undefined || tabHolds(tab, row, answers) ? index : -1)).filter((index) => index >= 0);
}
/** Bouton rapide d'une ligne (`row_action`) : `done` (la pastille), `action` (le bouton) ou `none`. */
export function rowActionState(template, sectionId, row, answers) {
    const action = placementOf(template, sectionId).row_action;
    if (action === undefined)
        return 'none';
    if (action.done_when !== undefined && holds(action.done_when, { row, answers }))
        return 'done';
    return action.visible_when === undefined || holds(action.visible_when, { row, answers }) ? 'action' : 'none';
}
/** La ligne après le bouton rapide : les valeurs déclarées posées sur la ligne, le reste inchangé. */
export function applyRowAction(template, sectionId, row) {
    const action = placementOf(template, sectionId).row_action;
    return action === undefined ? { ...row } : { ...row, ...action.values };
}
/** Sans casse ni accents (« Écarts » trouve « ecarts »). */
export function searchFold(value) {
    return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}
/** Une ligne répond-elle à la recherche ? Chaque mot de la recherche doit être dans le texte affiché de ses cellules. */
export function listSearchMatches(cells, query) {
    const words = searchFold(query).split(/\s+/).filter((word) => word !== '');
    if (words.length === 0)
        return true;
    const haystack = searchFold(cells.join(' '));
    return words.every((word) => haystack.includes(word));
}
// ---- Grille de 12 colonnes dans chaque partie (ADR-146 addendum (26)).
/**
 * Largeur de chaque champ sur 12 colonnes : `presentation.layout.span: full` = 12, `presentation.layout.width`
 * (1 à 12) déclarée, sinon 12 / colonnes de la partie (`placements.<id>.columns`, paysage 1 à 3, portrait 1 à 2,
 * web = paysage) ; au téléphone, toujours 12. Les champs se suivent et passent à la ligne quand la somme dépasse 12.
 */
export function fieldSpan(template, sectionId, entry, format) {
    if (format === 'mobile')
        return 12;
    const layout = entry.presentation?.layout;
    if (layout?.span === 'full')
        return 12;
    if (typeof layout?.width === 'number' && layout.width >= 1 && layout.width <= 12)
        return Math.round(layout.width);
    const columns = placementOf(template, sectionId).columns;
    const count = format === 'portrait' ? Math.min(2, Math.max(1, columns?.portrait ?? 1)) : Math.min(3, Math.max(1, columns?.landscape ?? 1));
    return 12 / count;
}
/** Rangées de la grille : les index des champs, par rangée, dans l'ordre (une rangée ne dépasse pas 12 colonnes). */
export function gridRows(spans) {
    const rows = [];
    let line = [];
    let used = 0;
    spans.forEach((span, index) => {
        const width = Math.min(12, Math.max(1, span));
        if (used + width > 12 && line.length > 0) {
            rows.push(line);
            line = [];
            used = 0;
        }
        line.push(index);
        used += width;
    });
    if (line.length > 0)
        rows.push(line);
    return rows;
}
//# sourceMappingURL=form-layout.js.map